import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { fromWireEnums } from '../entities/academy-enums.model';
import { ODataList } from '../entities/academy-entities.model';
import {
  DiscoverQuery,
  MinePathsQuery,
  ODataQuery,
} from '../entities/academy-query.model';
import {
  LearningPathCourseResponse,
  LearningPathCreateRequest,
  LearningPathResponse,
} from '../entities/academy.model';
import {
  CoverResponse,
  LearningPathCourseCreatedResponse,
  MineResult,
  MyLearningPathSummary,
  PathProgressResponse,
  toMineResult,
} from '../entities/academy-responses.model';
import { AcademyCrudService } from './academy-crud.service';
import { AcademyODataService } from './academy-odata.service';

/**
 * HTTP client for the Learning Paths area custom endpoints (`/LearningPaths/*`).
 * Source of truth: `docs/academy-api-integration.md` §7.9. Base reads over
 * `LearningPaths` use `AcademyODataService` (OData list); writes/by-id use
 * `AcademyCrudService`.
 */
@Injectable({ providedIn: 'root' })
export class LearningPathsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.academy_service;
  private readonly odata = inject(AcademyODataService);
  private readonly crud = inject(AcademyCrudService);

  /**
   * `GET /LearningPaths/mine` — paths containing at least one course the student has.
   * `status` defaults to `all`.
   */
  public mine(
    query: MinePathsQuery = {}
  ): Observable<MineResult<MyLearningPathSummary>> {
    return this.http
      .get<unknown>(`${this.baseUrl}/LearningPaths/mine`, {
        params: this.buildMineParams(query),
        withCredentials: true,
      })
      .pipe(map((res) => toMineResult<MyLearningPathSummary>(res)));
  }

  /** `GET /LearningPaths/discover` — published paths visible to the viewer. Plain array. */
  public discover(
    query: DiscoverQuery = {}
  ): Observable<LearningPathResponse[]> {
    return this.http
      .get<LearningPathResponse[]>(`${this.baseUrl}/LearningPaths/discover`, {
        params: this.buildDiscoverParams(query),
        withCredentials: true,
      })
      .pipe(map((items) => fromWireEnums(items)));
  }

  /**
   * `OPTIONS /LearningPaths` — producer-side OData list (drafts included, unlike
   * `discover`). The producer Trilhas list needs its own drafts; `discover` only
   * returns PUBLISHED paths visible to the viewer. Mirrors `CoursesService.list`.
   */
  public list(
    query: ODataQuery = {}
  ): Observable<ODataList<LearningPathResponse>> {
    return this.odata.list<LearningPathResponse>('LearningPaths', query);
  }

  /**
   * `GET /OData/LearningPathCourses?$filter=LearningPathId eq {id}&$orderby=Order` — the
   * path's ordered course links (`LearningPathCourseViewModel`: just ids + `Order`). Direct
   * analog of `CoursesService.listItems` (`/OData/CourseItems`): served at `/OData/` with a
   * plain GET, not the `OPTIONS /{Set}` convention. The set isn't in the doc's registered-set
   * list (§5) but follows the same `CourseItems` pattern that ships in production; callers
   * treat a failure as "no known courses" (the endpoint is OData-config, not test-confirmed).
   */
  public listCourses(
    pathId: string
  ): Observable<ODataList<LearningPathCourseResponse>> {
    return this.http.get<ODataList<LearningPathCourseResponse>>(
      `${this.baseUrl}/OData/LearningPathCourses`,
      {
        params: new HttpParams()
          .set('$filter', `LearningPathId eq ${pathId}`)
          .set('$orderby', 'Order'),
        withCredentials: true,
      }
    );
  }

  /**
   * `GET /LearningPaths/{id}/courses` — the path's ordered course links WITH title (the REST
   * sibling of the OData `listCourses`). Used by the producer edit screen so course rows render
   * titles without a `CourseId → coursesById` join. Plain array (not an OData envelope).
   */
  public getCourses(pathId: string): Observable<LearningPathCourseResponse[]> {
    return this.http.get<LearningPathCourseResponse[]>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(pathId)}/courses`,
      { withCredentials: true }
    );
  }

  /** `DELETE /LearningPaths/{id}/courses/{courseId}` — soft-delete a path-course link. */
  public removeCourse(pathId: string, courseId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(
        pathId
      )}/courses/${encodeURIComponent(courseId)}`,
      { withCredentials: true }
    );
  }

  /**
   * `PATCH /LearningPaths/{id}/courses/reorder` — bulk-atomic reorder; `orderedIds` is the full
   * set of **CourseId**s in the desired order (`Order = index`). A mismatched set → 400.
   */
  public reorderCourses(
    pathId: string,
    orderedIds: string[]
  ): Observable<void> {
    return this.http.patch<void>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(
        pathId
      )}/courses/reorder`,
      { OrderedIds: orderedIds }, // PascalCase: backend `ReorderRequest(IReadOnlyList<Guid> OrderedIds)`
      { withCredentials: true }
    );
  }

  /** `POST /LearningPaths/{id}/cover` — multipart cover image (field `file`). Mirrors course cover. */
  public setCover(pathId: string, file: File): Observable<CoverResponse> {
    const form = new FormData();
    form.append('file', file); // File carries its own name → multipart filename is preserved
    return this.http.post<CoverResponse>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(pathId)}/cover`,
      form,
      { withCredentials: true }
    );
  }

  /** `POST /LearningPaths/{id}/courses` — link a course at an order. ⚠500 if missing. */
  public addCourse(
    pathId: string,
    courseId: string,
    order: number
  ): Observable<LearningPathCourseCreatedResponse> {
    const params = new HttpParams()
      .set('courseId', courseId)
      .set('order', String(order));
    return this.http.post<LearningPathCourseCreatedResponse>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(pathId)}/courses`,
      {},
      { params, withCredentials: true }
    );
  }

  /** `POST /LearningPaths/{id}/publish` — publish (requires ≥1 course). ⚠404 / ⚠500. */
  public publish(pathId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(pathId)}/publish`,
      {},
      { withCredentials: true }
    );
  }

  /** `GET /LearningPaths/{id}/progress` — current viewer's path progress. 🔑 */
  public progress(pathId: string): Observable<PathProgressResponse> {
    return this.http.get<PathProgressResponse>(
      `${this.baseUrl}/LearningPaths/${encodeURIComponent(pathId)}/progress`,
      { withCredentials: true }
    );
  }

  /** `POST /LearningPaths` — create a learning path (generic OData create; `LearningPathId` is server-assigned). */
  public create(
    viewModel: LearningPathCreateRequest
  ): Observable<LearningPathResponse> {
    return this.crud.create<LearningPathResponse>('LearningPaths', viewModel);
  }

  /**
   * `GET /LearningPaths?id={guid}` — read one path by key. Unlike `discover`/`list`, the single
   * read carries `CoverUrl`, so the edit builder can show the persisted cover.
   */
  public getById(pathId: string): Observable<LearningPathResponse | null> {
    return this.crud.getById<LearningPathResponse>('LearningPaths', pathId);
  }

  /** `DELETE /LearningPaths?id={guid}` — remove a learning path (OData delete). */
  public remove(pathId: string): Observable<void> {
    return this.crud.delete('LearningPaths', pathId);
  }

  private buildDiscoverParams(query: DiscoverQuery): HttpParams {
    let params = new HttpParams();
    // App scope is resolved server-side from the user's account (feature 010) — never send
    // `?product=`. `query.product` is kept on the type as accepted-but-ignored until the
    // admin manual filter UI is retired.
    if (query.search) params = params.set('search', query.search);
    return params;
  }

  private buildMineParams(query: MinePathsQuery): HttpParams {
    let params = new HttpParams();
    if (query.status) params = params.set('status', query.status);
    if (query.top != null) params = params.set('top', String(query.top));
    if (query.skip != null) params = params.set('skip', String(query.skip));
    return params;
  }
}
